import { privateRequest } from "../axios";
import type { ListApiResponse } from "@/types/common";
import type { EventType } from "./events";

// Confirmed against a real authenticated GET /api/admin/venues response,
// given directly by the user — replaces decision #47's Figma-mock guess.
// No `status` field exists on the real record at all (the guessed
// preferred/active/holding badge was fabricated); `suitableFor` is an
// uppercase enum (`MARQUEE`, `MASTERCLASS` confirmed on a real venue, and
// reusing `EventType` from services/api/events.ts rather than a separate
// guess — the same three gathering types, "MICRO_MEETUPS" plural
// included, confirmed there from a real `GET /events` response), and
// `ratePerEvening` is a bare numeric string, not a display-ready label
// like "Owned"/"No charge".
export interface Venue {
  id: number;
  name: string;
  chapterId: number;
  chapterName: string;
  address: string;
  suitableFor: EventType[];
  lastUsedAt: string | null;
}

export interface GetVenuesParams {
  pageNo?: number;
  perPage?: number;
  // Filters for VenuesSection.tsx's table header — venue name search,
  // city (chapter), a "used for" gathering type, and a last-used window
  // (full ISO-8601 datetimes, backend `OffsetDateTime` params). Key names
  // given directly by the user from the backend controller.
  search?: string | undefined;
  cityId?: number | undefined;
  eventType?: EventType | undefined;
  lastUsedFromDate?: string | undefined;
  lastUsedToDate?: string | undefined;
}

// Real payload shape given directly by the user. `venueId: 0` for a new
// venue, same "explicit 0 for a not-yet-created id" convention
// decision #45 used for chapters.
export interface CreateVenuePayload {
  // venueId: number;
  name: string;
  chapterId: number;
  // Optional — the address field is currently removed from AddVenueModal.
  address?: string;
  suitableFor: EventType[];
}

// Per the user: editing reuses the exact same `POST /admin/venues`
// endpoint and method as `createVenue` below, same body, with a real
// `venueId` added — mirrors chapters' `updateChapter` convention
// (services/api/chapters.ts).
export interface UpdateVenuePayload extends CreateVenuePayload {
  venueId: number;
}

const ENDPOINTS = {
  VENUES: "/venues",
};

const getVenues = async (params?: GetVenuesParams) => {
  const response = await privateRequest.get<ListApiResponse<Venue>>(ENDPOINTS.VENUES, { params });
  return response.data;
};

// Real endpoint given directly by the user: GET /admin/venues/chapter/{id}
// — venues for a single chapter, used by CreateGathering.tsx's "Venue"
// select once a city/chapter is picked. Same `ListApiResponse<Venue>`
// envelope as the plain list endpoint, confirmed via a real sample
// response.
const getVenuesByChapter = async (chapterId: number) => {
  const response = await privateRequest.get<ListApiResponse<Venue>>(`${ENDPOINTS.VENUES}/chapter/${chapterId}`);
  return response.data;
};

const createVenue = async (payload: CreateVenuePayload) => {
  const response = await privateRequest.post(ENDPOINTS.VENUES, payload);
  return response.data;
};

const updateVenue = async (payload: UpdateVenuePayload) => {
  const response = await privateRequest.post(ENDPOINTS.VENUES, payload);
  return response.data;
};

// Real endpoint given directly by the user: DELETE /admin/venues/{id}.
const deleteVenue = async (venueId: number) => {
  const response = await privateRequest.delete(`${ENDPOINTS.VENUES}/${venueId}`);
  return response.data;
};

const venueService = {
  getVenues,
  getVenuesByChapter,
  createVenue,
  updateVenue,
  deleteVenue,
};

export default venueService;
